import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  type CSSProperties,
  type MouseEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/cn";

import { RevealGroup, RevealText } from "@/components/landing/RevealText";

const SLIDES = [
  {
    video: "/videos/landing/interface-1.mp4",
    widget: "/images/landing/interface/risk-card.png",
    alt: "Виджет «Установить лимит»",
    label: "Настройки торгового риска",
    widgetClassName: "w-[34.3125rem]",
  },
  {
    video: "/videos/landing/interface-2.mp4",
    widget: "/images/landing/interface/balance-card.png",
    alt: "Баланс бота: $2506, +58% за 24 часа",
    label: "Баланс и доходность",
    widgetClassName: "w-[34.375rem]",
  },
  {
    video: "/videos/landing/interface-3.mp4",
    widget: "/images/landing/interface/history-card.png",
    alt: "История сделок за месяц",
    label: "История сделок",
    widgetClassName: "w-[36.375rem]",
  },
];

const SWIPE_THRESHOLD_PX = 45;

const mod = (value: number, length: number) => ((value % length) + length) % length;

/** -1 за экраном слева, 0 в кадре, 1 кромкой справа */
const slotOf = (slideIndex: number, index: number) =>
  mod(slideIndex - index + 1, SLIDES.length) - 1;

export const InterfaceSection = () => {
  const [{ index, step }, setPosition] = useState({ index: 0, step: 1 });
  const [side, setSide] = useState<"prev" | "next">("next");
  const [isHovered, setIsHovered] = useState(false);
  const [stageRef, isSectionVisible] = useInView<HTMLDivElement>({
    rootMargin: "0px 0px -30% 0px",
  });

  // На первом слайде: на сцене уже висит постоянный наблюдатель
  const [introRef, hasIntroPlayed] = useInView<HTMLElement>({
    once: true,
    rootMargin: "0px 0px -30% 0px",
  });

  const hintRef = useRef<HTMLButtonElement>(null);
  const pointer = useRef({ x: 0, y: 0 });
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const frame = useRef(0);

  const advance = useCallback(
    (direction: number) =>
      setPosition((position) => ({ index: position.index + direction, step: direction })),
    []
  );

  // При перелистывании не трогаем: слайд приехал бы застывшим кадром
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    stage.querySelectorAll<HTMLVideoElement>("video").forEach((video) => {
      if (isSectionVisible) void video.play().catch(() => undefined);
      else video.pause();
    });
  }, [isSectionVisible, stageRef]);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const syncHint = () => {
    frame.current = 0;
    const hint = hintRef.current;
    if (!hint) return;
    hint.style.setProperty("--hint-x", `${pointer.current.x}px`);
    hint.style.setProperty("--hint-y", `${pointer.current.y}px`);
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    pointer.current = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };

    setSide(event.clientX < bounds.left + bounds.width / 2 ? "prev" : "next");
    setIsHovered(true);

    if (!frame.current) frame.current = requestAnimationFrame(syncHint);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    // После свайпа прилетает синтетический клик
    if (touchStart.current !== null) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    advance(event.clientX < bounds.left + bounds.width / 2 ? -1 : 1);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    if (!start) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    // Длиннее вертикального, иначе перехватим прокрутку страницы
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      advance(dx < 0 ? 1 : -1);
    }

    requestAnimationFrame(() => {
      touchStart.current = null;
    });
  };

  const isPrev = side === "prev";

  return (
    <section className="landing-carousel-section border-landing-line overflow-hidden border-b pt-[6.25rem] pb-[3.75rem]">
      <RevealGroup className="landing-container flex flex-col gap-2 text-center text-[1rem] leading-[1.4] font-semibold">
        <RevealText
          as="h2"
          className="text-landing-ink"
        >
          Максимально упростили интерфейс
        </RevealText>
        <RevealText
          as="p"
          delay={70}
          className="text-landing-ink/50"
        >
          Акцент на том, что действительно для вас важно
        </RevealText>
      </RevealGroup>

      <div
        ref={stageRef}
        data-revealed={hasIntroPlayed ? "" : undefined}
        className="landing-carousel relative mt-12 h-[var(--slide-h)] outline-none"
        role="group"
        tabIndex={0}
        aria-label="Карусель интерфейса. Листается стрелками влево и вправо"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") advance(-1);
          if (event.key === "ArrowRight") advance(1);
        }}
      >
        {SLIDES.map((slide, slideIndex) => {
          const slot = slotOf(slideIndex, index);
          // Перелетающий с края на край переставляется мгновенно
          const isWrapping = step > 0 ? slot === 1 : slot === -1;

          return (
            <article
              key={slide.video}
              ref={slideIndex === 0 ? introRef : undefined}
              style={{ "--slot": slot, zIndex: slot === 0 ? 2 : 1 } as CSSProperties}
              data-wrapping={isWrapping ? "" : undefined}
              aria-hidden={slot !== 0}
              className="landing-slide absolute top-0 left-0 h-full w-[var(--slide-w)] overflow-hidden rounded-3xl bg-neutral-900"
            >
              <video
                className="absolute inset-0 h-full w-full object-cover"
                src={slide.video}
                muted
                loop
                playsInline
                preload="auto"
              />
              <img
                src={slide.widget}
                alt={slide.alt}
                className={cn(
                  "landing-slide-widget pointer-events-none absolute top-1/2 left-1/2 max-w-[80%] -translate-x-1/2 -translate-y-1/2 select-none",
                  slide.widgetClassName
                )}
              />
              <div
                aria-hidden
                className="landing-slide-sheen"
              />
            </article>
          );
        })}

        <button
          ref={hintRef}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            advance(isPrev ? -1 : 1);
          }}
          data-visible={isHovered ? "" : undefined}
          className={cn(
            "landing-carousel-hint absolute z-20 flex h-[3.125rem] w-[8.5rem] cursor-pointer items-center justify-between gap-2 rounded-3xl",
            "max-lg:hidden",
            "border-4 border-white/31 bg-[rgb(25_23_19/0.6)] backdrop-blur-[42px]",
            "text-trim text-[0.875rem] leading-[1.2] font-semibold tracking-[0.02em] text-white",
            isPrev ? "flex-row-reverse pr-6 pl-4" : "pr-4 pl-6"
          )}
        >
          <span>{isPrev ? "Назад" : "Дальше"}</span>
          {isPrev ? (
            <ChevronLeft
              className="size-4 shrink-0"
              aria-hidden
            />
          ) : (
            <ChevronRight
              className="size-4 shrink-0"
              aria-hidden
            />
          )}
        </button>

        <div className="absolute bottom-4 left-1/2 hidden -translate-x-1/2 gap-2 max-lg:flex">
          {SLIDES.map((slide, dotIndex) => (
            <button
              key={slide.video}
              type="button"
              aria-label={slide.label}
              aria-current={dotIndex === mod(index, SLIDES.length)}
              onClick={(event) => {
                event.stopPropagation();
                // Ближайшее направление: при прыжке край меняли бы двое
                advance(mod(dotIndex - index + 1, SLIDES.length) - 1);
              }}
              className={cn(
                "size-2 rounded-full transition-colors duration-300",
                dotIndex === mod(index, SLIDES.length) ? "bg-white" : "bg-white/40"
              )}
            />
          ))}
        </div>

        <span
          className="sr-only"
          aria-live="polite"
        >
          {SLIDES[mod(index, SLIDES.length)].label}
        </span>
      </div>
    </section>
  );
};
